/**
 * Shared HTTP contract for a Trip — groups a traveler's vouchers belonging to
 * one journey (round-trip flight + hotel + mobility …). Part of the `traveler`
 * namespace (API-04). Embeds {@link VoucherViewSchema} so a consumer (a support
 * agent / AI) sees the whole trip in one response.
 *
 * This shape is the app-facing trip view. Historically it was produced by
 * customer-api by deriving trips from vouchers; the platform now also has
 * durable trips in trip-api. Keep this contract source-agnostic so web-chat can
 * survive the rollout from customer-derived trips to trip-api app trips.
 *
 * `name` (traveler-facing, generated pt-BR) and `status` (lifecycle bucket) are
 * derived/presentation fields and remain nullable/optional for compatibility.
 * `startAt`/`endAt`/`origin`/`destination` are nullable because some legacy
 * voucher groupings or early durable trips may not have a complete journey yet.
 */
import { z } from 'zod';

import { DestinationImageViewSchema } from './destination_image_view';
import { VoucherViewSchema } from './traveler_view';
import { TripFulfillmentSchema } from './trip_fulfillment';

/**
 * Traveler-facing trip lifecycle bucket — WHEN the trip is — derived from the trip
 * window and its vouchers' canonical statuses (trip-api `deriveAppTripStatus`).
 * Whether what was booked is guaranteed is another axis: `TripFulfillmentSchema`.
 */
export const TripStatusSchema = z.enum(['UPCOMING', 'ONGOING', 'COMPLETED', 'CANCELLED']);
export type TripStatus = z.infer<typeof TripStatusSchema>;

/**
 * Trip ordering strategy — the cross-service `sort` contract for the trips read
 * surface. The agents trip tool sends it; customer-api `/trips` validates it and
 * defaults to `proximity` when omitted (ongoing/upcoming first, then past).
 * Single value today; this is the one place to add orderings without drift.
 */
export const TripSortSchema = z.enum(['proximity']);
export type TripSort = z.infer<typeof TripSortSchema>;

/**
 * Trip scope — the cross-service `scope` contract for the trips read surface.
 * `open` (default in the agents trip tool) keeps only trips that have not yet
 * ended (active/upcoming) plus undated ones; `all` includes past (completed/
 * cancelled) trips. The agents tool sends `open` by default and flips to `all`
 * only when the traveler asks about past trips. customer-api `/trips` validates
 * it; an OMITTED param means `all` (backward-compatible — the web home card path
 * sends none and still gets every trip).
 */
export const TripScopeSchema = z.enum(['open', 'all']);
export type TripScope = z.infer<typeof TripScopeSchema>;

/** Quem escreveu o nome da viagem — eixo de PROVENIÊNCIA, não `MessageAuthor` nem
 *  `AgentKind`. `traveler` (a pessoa digitou no painel) e `system` (a plataforma deu o
 *  nome, como o do evento corporativo na viagem por chave externa) são gravados; o enum
 *  ainda admite `agent` porque a v4 do documento do trip-api o introduziu e um documento
 *  gravado com ele precisa continuar parseando. */
export const TripNameSourceSchema = z.enum(['traveler', 'agent', 'system']);

/** O nome da viagem: a regra única do rename e da viagem por chave externa. Limite de 80:
 *  o nome vive num `<h1>` e numa linha de sidebar. `trim` porque o documento aceita
 *  string vazia e um `<h1>` vazio é pior que um nome feio. */
const TripNameSchema = z.string().trim().min(1).max(80);

/** Corpo do rename da viagem. A MESMA regra vale nas duas bordas — a do app
 *  (app-integration, com o token do chat) e a interna (trip-api) —, então ela mora
 *  aqui e não é redefinida em nenhuma das duas. `source` segue aceito (`traveler` ou `agent`) só por
 *  compatibilidade de fio: o contrato antigo o aceitava e um cliente já publicado pode
 *  mandá-lo, e a borda não pode passar a recusar esse corpo com 400. O valor NÃO muda
 *  mais a escrita — nenhuma das duas bordas o lê, e o trip-api grava sempre `traveler`.
 *  `.strict()` continua recusando qualquer outra chave. */
export const TripRenameRequestSchema = z
  .object({
    name: TripNameSchema,
    source: TripNameSourceSchema.exclude(['system']).optional(),
  })
  .strict();
export type TripRenameRequest = z.infer<typeof TripRenameRequestSchema>;

/** Corpo do "garanta a viagem desta chave" (trip-api interno): a viagem de um dono é
 *  achada ou criada pela chave externa que o chamador controla (`mice:<projeto>:<viajante>`
 *  para o convidado de um evento), sem o chamador guardar o id. `dateRange` é o intervalo
 *  inicial; as orders o reescrevem depois (`refreshTripSummary`). Nome com a mesma regra do
 *  rename. `.strict()` recusa qualquer outra chave — o dono vem da chamada, não do corpo. */
export const TripKeyedEnsureRequestSchema = z
  .object({
    key: z.string().min(1).max(200),
    name: TripNameSchema,
    dateRange: z
      .object({ start: z.iso.datetime().nullable(), end: z.iso.datetime().nullable() })
      .optional(),
  })
  .strict();
export type TripKeyedEnsureRequest = z.infer<typeof TripKeyedEnsureRequestSchema>;

/** A resposta: o id da viagem, se esta chamada a criou e se ela está arquivada (uma
 *  viagem arquivada por falta de voo volta a ser a mesma quando a chave reaparece). */
export const TripKeyedEnsureResponseSchema = z.object({
  id: z.string(),
  created: z.boolean(),
  archived: z.boolean(),
});
export type TripKeyedEnsureResponse = z.infer<typeof TripKeyedEnsureResponseSchema>;

/** O estado da viagem para quem decide escondê-la (o passe do mice-api): se ela já está
 *  arquivada e cada order que ela carrega, com o ciclo de vida (`PENDING | FAILED | STALE`) e o
 *  solicitante que a reservou (`correlation.facts.requesterId`) — o chamador separa a compra
 *  dele do que o viajante (ou outro) pôs na mesma viagem. */
export const TripStateResponseSchema = z.object({
  archived: z.boolean(),
  orders: z.array(z.object({ status: z.string(), requesterId: z.string() })),
});
export type TripStateResponse = z.infer<typeof TripStateResponseSchema>;

/** A resposta do rename: o nome GRAVADO, para o chamador re-renderizar sem um segundo
 *  round-trip. É contrato de fio de verdade — o trip-api produz, a app-integration
 *  parseia —, então mora aqui e não em duas cópias que divergem no primeiro ajuste. */
export const TripRenameResponseSchema = z.object({ id: z.string(), name: z.string() });
export type TripRenameResponse = z.infer<typeof TripRenameResponseSchema>;

/** Corpo do arquivar/desarquivar da viagem: um flag só, nas duas direções. NÃO é o
 *  "archive" que apaga a viagem esvaziada (`DELETE .../trips/{tripId}` no trip-api,
 *  `POST /trips/:tripId/archive` no BFF): arquivar aqui é ESCONDER uma viagem com
 *  conteúdo da lista e da home, reversível. Mesma régua do rename: a regra vale nas duas
 *  bordas (app-integration com o token do chat, trip-api interno), então mora aqui. */
export const TripArchivedRequestSchema = z.object({ archived: z.boolean() }).strict();
export type TripArchivedRequest = z.infer<typeof TripArchivedRequestSchema>;

/** A resposta: o estado GRAVADO, para o chamador re-renderizar sem segundo round-trip
 *  (mesmo contrato de fio do rename). */
export const TripArchivedResponseSchema = z.object({ id: z.string(), archived: z.boolean() });
export type TripArchivedResponse = z.infer<typeof TripArchivedResponseSchema>;

/** Destino do move de um voucher: viagem EXISTENTE ou viagem NOVA, nunca os dois e
 *  nunca nenhum. União discriminada, e não dois campos opcionais: com opcionais, o
 *  corpo vazio e o par completo passariam na validação e o handler teria de defender
 *  de novo — a borda é o lugar de recusar, não o meio.
 *
 *  `newTripName` é só o NOME. A viagem não guarda data própria: `refreshTripSummary`
 *  deriva `dateRange` das orders depois do move, então pedir data aqui seria coletar
 *  um valor que o servidor descarta. */
export const VoucherMoveRequestSchema = z.union([
  z.object({ tripId: z.string().min(1) }).strict(),
  z.object({ newTripName: z.string().trim().min(1).max(80) }).strict(),
]);
export type VoucherMoveRequest = z.infer<typeof VoucherMoveRequestSchema>;

/** As DUAS pontas do move. `fromTripId` porque a origem pode ter ficado vazia e quem
 *  chama precisa saber qual re-render invalidar; `movedVoucherIds` porque a unidade de
 *  movimento é a ORDER, e uma order pode carregar irmãos (ida e volta na mesma reserva)
 *  que foram junto sem terem sido escolhidos. `fromTripId` é `null` quando o voucher não
 *  morava em viagem nenhuma (attach de pendente, spec 2026-09-14). */
export const VoucherMoveResponseSchema = z.object({
  fromTripId: z.string().nullable(),
  toTripId: z.string(),
  movedVoucherIds: z.array(z.string()),
});
export type VoucherMoveResponse = z.infer<typeof VoucherMoveResponseSchema>;

/** O desvincular pelo viajante (#4989): o voucher sai de `fromTripId` e fica SEM viagem,
 *  com a marca que impede o encaixe automático de religá-lo. `relinkable` diz se ele
 *  pode voltar pelo mesmo caminho do pendente (o attach) — emitido, do próprio viajante e
 *  com a jornada ainda por terminar; é o que decide se a tela oferece o Desfazer. */
export const VoucherUnlinkResponseSchema = z.object({
  voucherId: z.string(),
  fromTripId: z.string(),
  relinkable: z.boolean(),
});
export type VoucherUnlinkResponse = z.infer<typeof VoucherUnlinkResponseSchema>;

/** Voo remarcado: o voucher novo (`voucherId`) substitui o antigo com alteração
 *  solicitada (`replacesVoucherId`) na mesma viagem e do mesmo viajante.
 *  `sameLocator` = os dois têm o mesmo localizador; senão casaram pelo trecho. */
export const TripRebookingSchema = z.object({
  voucherId: z.string(),
  replacesVoucherId: z.string(),
  sameLocator: z.boolean(),
});
export type TripRebooking = z.infer<typeof TripRebookingSchema>;

export const TripViewSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  companyId: z.string().nullable(),
  // Rótulo DERIVADO das orders, em pt-BR fixo ("Viagem CNF → GRU · 12-18 jul 2026").
  // Consumidor de tela deve preferir `manualName` e, na ausência dele, montar o
  // rótulo no idioma da própria tela a partir de origin/destination/startAt/endAt —
  // este campo não é localizado.
  name: z.string().nullable().optional(),
  // O nome que o VIAJANTE escreveu. Quando presente, ele É o nome da viagem e ganha
  // do derivado em toda superfície. `.optional()` para produtor antigo continuar
  // parseando.
  manualName: z.string().min(1).nullable().optional(),
  status: TripStatusSchema.nullable().optional(), // derived lifecycle bucket
  // Rollup of the live vouchers (`deriveTripFulfillment`), stamped by trip-api over
  // the SAME `vouchers` below, so a client that falls back to deriving it reads the
  // same answer. `null` = nothing to promise. `.optional()`: same deploy-skew
  // contract as `servicesDegraded` — an older producer omits it.
  fulfillment: TripFulfillmentSchema.nullable().optional(),
  origin: z.string().nullable(),
  destination: z.string().nullable(),
  startAt: z.coerce.date().nullable(),
  endAt: z.coerce.date().nullable(),
  // Airport-local window display strings (offset-less ISO) — the local
  // wall-clock of the voucher whose parsed startAt/endAt won the trip window,
  // so the client renders the range with no re-parse. Without these the route's
  // `...t` spread would silently strip them at the wire boundary. `.optional()`
  // keeps the on-read fallback path (TripView built from a Trip directly) green.
  startLocal: z.string().nullable().optional(),
  endLocal: z.string().nullable().optional(),
  // Viagem ABERTA (só ida: o último transporte não volta à cidade de onde ela partiu):
  // o último dia (data-só, meia-noite UTC, como `endAt`) em que ela segue ATIVA depois
  // do `endAt` — a véspera da próxima viagem do viajante, com teto de 30 dias depois da
  // última partida. O viajante continua no destino, então a viagem é "em curso", não
  // "concluída" (`isTripDone`). `endAt` segue sendo o fim que as reservas conhecem.
  // Producer sets it only when it reaches past `endAt`; absent = viagem fechada OR an
  // older producer. `.optional()`: same deploy-skew contract as `servicesDegraded`.
  activeUntil: z.coerce.date().optional(),
  modals: z.array(z.string()),
  totalAmount: z.number().nullable(),
  currency: z.string().nullable(),
  // Destination photo enrichment. Historically stamped by the removed
  // customer-api materialized projection; going forward this belongs to the
  // trip-api app read model. Optional/null keeps trip reads fail-open.
  destinationImage: DestinationImageViewSchema.nullable().optional(),
  // ≥1 linked voucher failed the live read-through (customer-api error/429), so
  // `vouchers` may be incomplete — the trip still renders (fail-open), but the
  // client must not present the gap as "no services". Producer sets it only when
  // true; absent = healthy read OR an older producer, both meaning "trust
  // `vouchers` as-is". `.optional()` is the wire contract: never required, never
  // removed (deploy skew).
  servicesDegraded: z.boolean().optional(),
  // Fail-safe de conferência (#2252): todos os vouchers VINCULADOS estão mortos,
  // mas existe voucher EMITIDO do mesmo viajante na janela da viagem SEM vínculo
  // (reemissão via crédito/transbordo no legado é o caso típico). O produtor NÃO
  // declara a viagem CANCELLED nesse estado — o cancelamento seria mentira com
  // risco de recompra — e marca a conferência aqui. Producer sets it only when
  // true; absent = sem conflito OR produtor antigo. `.optional()`: mesmo contrato
  // de deploy-skew do `servicesDegraded`.
  needsReview: z.boolean().optional(),
  // A trip the OWNER (travelerRef) does not travel on: every linked voucher has a
  // traveler other than the owner. Today this is the requester's booking
  // write-ahead trip (modal-session creates it with travelerRef = requesterId).
  // The requester board is where these trips live; the sidebar/home hide them.
  // Producer sets it only when true; absent = the owner travels OR an older
  // producer. `.optional()`: same deploy-skew contract as `servicesDegraded`.
  requestedForOthers: z.boolean().optional(),
  // O viajante ARQUIVOU a viagem (`archivedAt` no trip-api): ela sai da sidebar, da
  // home e do carrossel do web-chat, mas segue na lista completa — a rota
  // `/trips/:id`, as cercas de posse do BFF e o desarquivar precisam dela. Producer
  // sets it only when true; absent = viagem à vista OR an older producer.
  // `.optional()`: same deploy-skew contract as `servicesDegraded`.
  archived: z.boolean().optional(),
  // Os pares antigo→novo dos voos remarcados. O antigo continua em `vouchers` (a tela
  // mostra a linha "Antes"). Producer sets it only when non-empty; `.optional()`
  // because web-chat and trip-api deploy separately (same skew as `servicesDegraded`).
  rebookings: z.array(TripRebookingSchema).optional(),
  vouchers: z.array(VoucherViewSchema),
});
export type TripView = z.infer<typeof TripViewSchema>;
